import * as R from 'ramda';
import { differenceInDays, getDay, isDate, isSameMonth, isThisHour, isThisMonth, isThisWeek, isThisYear, isValid, format, getDate, } from 'date-fns';
import { isEqual, addDays, addHours, addMonths, addWeeks, addYears, endOfDay, endOfHour, endOfMonth, endOfWeek, endOfYear, format as formatC, startOfDay, startOfHour, startOfMonth, startOfWeek, startOfYear, subDays, subHours, subMonths, subWeeks, subYears, } from 'date-fns/fp';
import { is, any, map, range } from 'ramda';
import { tz, TZDate } from '@date-fns/tz';
import { flow } from 'fp-ts/lib/function';
export const ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ss.sssZ';
// 2021-01-01T00:00:00.800Z
export const fullIsoDateRegex = /^[0-9]{4}-[10][0-9]-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}Z$/;
export const isFullIsoDate = (date) => date.match(fullIsoDateRegex);
export const getMonthsOfYear = (type) => range(0, 12)
    .map((number) => new Date(2020, number, 1))
    .map(formatC(type === 'full' ? 'LLLL' : type === 'condensed' ? 'LLL' : 'LLLLL'));
export const monthDisplay = (date, type) => formatC(type === 'full' ? 'LLLL' : type === 'condensed' ? 'LLL' : 'LLLLL', date);
export const getCurrentMonth = (type, currentDate = new Date()) => formatC(type === 'full' ? 'LLLL' : type === 'condensed' ? 'LLL' : 'LLLLL', currentDate);
// prettier-ignore
export const dayAbbreviations = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const mobileDayAbbreviations = [
    'Su',
    'Mo',
    'Tu',
    'We',
    'Th',
    'Fr',
    'Sa',
];
export const FULL_MONTHS_IN_YEAR = getMonthsOfYear('full');
// Week Helpers
export const startOfPreviousDay = flow(subDays(1), startOfDay);
export const startOfNextDay = flow(addDays(1), startOfDay);
export const endOfPreviousDay = flow(subDays(1), endOfDay);
export const endOfNextDay = flow(addDays(1), endOfDay);
export const thisDayInterval = (date) => ({
    start: startOfDay(date),
    end: endOfDay(date),
});
export const nextDayInterval = (date) => ({
    start: startOfNextDay(date),
    end: endOfNextDay(date),
});
export const previousDayInterval = (date) => ({
    start: startOfPreviousDay(date),
    end: endOfPreviousDay(date),
});
// Week Helpers
export const startOfPreviousWeek = flow(subWeeks(1), startOfWeek);
export const startOfNextWeek = flow(addWeeks(1), startOfWeek);
export const endOfPreviousWeek = flow(subWeeks(1), endOfWeek);
export const endOfNextWeek = flow(addWeeks(1), endOfWeek);
export const thisWeekInterval = (date) => ({
    start: startOfWeek(date),
    end: endOfWeek(date),
});
export const nextWeekInterval = (date) => ({
    start: startOfNextWeek(date),
    end: endOfNextWeek(date),
});
export const previousWeekInterval = (date) => ({
    start: startOfPreviousWeek(date),
    end: endOfPreviousWeek(date),
});
export const anyThisWeek = any(isThisWeek);
export const anyNextWeek = flow(map(addWeeks(1)), anyThisWeek);
export const anyPreviousWeek = flow(map(subWeeks(1)), anyThisWeek);
// Month Helpers
export const startOfPreviousMonth = flow(subMonths(1), startOfMonth);
export const startOfNextMonth = flow(addMonths(1), startOfMonth);
export const endOfPreviousMonth = flow(subMonths(1), endOfMonth);
export const endOfNextMonth = flow(addMonths(1), endOfMonth);
export const thisMonthInterval = (date) => ({
    start: startOfMonth(date),
    end: endOfMonth(date),
});
export const nextMonthInterval = (date) => ({
    start: startOfNextMonth(date),
    end: endOfNextMonth(date),
});
export const previousMonthInterval = (date) => ({
    start: startOfPreviousMonth(date),
    end: endOfPreviousMonth(date),
});
export const anyThisMonth = any(isThisMonth);
export const anyNextMonth = flow(map(addMonths(1)), anyThisMonth);
export const anyPreviousMonth = flow(map(subMonths(1)), anyThisMonth);
// Year Helpers
export const startOfPreviousYear = flow(subYears(1), startOfYear);
export const startOfNextYear = flow(addYears(1), startOfYear);
export const endOfPreviousYear = flow(subYears(1), endOfYear);
export const endOfNextYear = flow(addYears(1), endOfYear);
export const thisYearInterval = (date) => ({
    start: startOfYear(date),
    end: endOfYear(date),
});
export const nextYearInterval = (date) => ({
    start: startOfNextYear(date),
    end: endOfNextYear(date),
});
export const previousYearInterval = (date) => ({
    start: startOfPreviousYear(date),
    end: endOfPreviousYear(date),
});
export const anyThisYear = any(isThisYear);
export const anyNextYear = flow(map(addYears(1)), anyThisYear);
export const anyPreviousYear = flow(map(subYears(1)), anyThisYear);
// Hour Helpers
export const startOfPreviousHour = flow(subHours(1), startOfHour);
export const startOfNextHour = flow(addHours(1), startOfHour);
export const endOfPreviousHour = flow(subHours(1), endOfHour);
export const endOfNextHour = flow(addHours(1), endOfHour);
export const thisHourInterval = (date) => ({
    start: startOfHour(date),
    end: endOfHour(date),
});
export const nextHourInterval = (date) => ({
    start: startOfNextHour(date),
    end: endOfNextHour(date),
});
export const previousHourInterval = (date) => ({
    start: startOfPreviousHour(date),
    end: endOfPreviousHour(date),
});
export const anyThisHour = any(isThisHour);
export const anyNextHour = flow(map(addHours(1)), anyThisHour);
export const anyPreviousHour = flow(map(subHours(1)), anyThisHour);
export const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21)
        return 'th'; // covers 11th to 19th
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};
export const displayOrdinalNumber = (n) => `${n}${getOrdinalSuffix(n)}`;
export const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const formatTimezone = (timezone, format, options) => {
    const timezoneContext = typeof timezone === 'string'
        ? new TZDate(new Date(), timezone)
        : timezone.in(new Date());
    if (options?.blankStringIfMatchesUserTimezone &&
        timezoneContext.timeZone === userTimezone)
        return '';
    return [
        options?.wrapWithParentheses ? '(' : '',
        timezoneContext
            .toLocaleDateString(undefined, {
            day: '2-digit',
            // timeZoneName: 'short',
            timeZoneName: format,
        })
            .substring(4),
        options?.wrapWithParentheses ? ')' : '',
    ].join('');
};
const isTZDate = (date) => isDate(date) && 'timeZone' in date;
export const formatDate = (strFormat, date, 
// If the specified timezone is different from the users, the date will be
// rendered in the specified timezone, and that timezone will be appended
// for clarity
timezone, 
// If the event is rendered in a different timezone as spec'd by `timezone`
// should the timezone text (e.g. (EDT)) be hidden (defaults to appending this text)
hideTimezone) => {
    if (!date)
        return 'unknown date';
    const intrinsicTimezone = isTZDate(date) && date.timeZone ? { in: tz(date.timeZone) } : undefined;
    const options = typeof timezone === 'string'
        ? { in: tz(timezone) }
        : (timezone ?? intrinsicTimezone ?? undefined);
    const resolvedTimezone = typeof options === 'string' ? options : options?.in(new Date()).timeZone;
    const timezoneString = resolvedTimezone && !hideTimezone
        ? formatTimezone(resolvedTimezone, 'short', {
            blankStringIfMatchesUserTimezone: true,
            wrapWithParentheses: true,
        })
        : '';
    const d = new Date(date);
    if (strFormat === 'seconds')
        return format(d, 'T', options);
    if (strFormat === 'month')
        return format(d, 'LLLL', options);
    if (strFormat === 'month-year')
        return format(d, 'LLLL yyyy', options);
    if (strFormat === 'abbreviated-month')
        return format(d, 'LLL', options);
    if (strFormat === 'day')
        return format(d, 'EEEE', options);
    if (strFormat === 'day-time')
        return `${format(d, 'EEEE', options)}, ${[
            format(d, 'p', options),
            timezoneString,
        ].join(' ')}`;
    if (strFormat === 'abbreviated-day')
        return format(d, 'E', options);
    if (strFormat === 'day-first-letter')
        return format(d, 'EEEEE', options);
    if (strFormat === 'day-of-year')
        return format(d, 'D', options);
    if (strFormat === 'day-of-month')
        return format(d, 'd', options);
    if (strFormat === 'week-of-year')
        return format(d, 'l', options);
    if (strFormat === 'form')
        return format(d, 'yyyy-MM-dd', options);
    const time = [format(d, 'p', options), timezoneString].join(' ');
    const condensedTime = time.replace(':00', '').replace(' ', '').trim();
    if (strFormat === 'time')
        return time;
    if (strFormat === 'week-of-month')
        return getWeekOfMonth(d).toString();
    if (strFormat === 'condensed-readable')
        return format(d, 'E, LLL d', options);
    if (strFormat === 'abbreviated-time')
        return condensedTime.replace(/(AM|PM)/, '');
    if (strFormat === 'condensed-time')
        return condensedTime.toLowerCase();
    if (strFormat === 'month-week') {
        // Format: "Jan, Week #1"
        const firstOfMonth = startOfMonth(date);
        const offset = getDay(firstOfMonth); // day of the week of the 1st
        const dayOfMonth = getDate(date);
        const adjustedDay = dayOfMonth + offset;
        const weekNumber = Math.ceil(adjustedDay / 7);
        return `${format(date, 'MMM', options)}, Week #${weekNumber}`;
    }
    if (strFormat === 'condensed-readable-date-and-time')
        return [format(d, 'E, LLL d, h:mmaaa', options), timezoneString].join(' ');
    if (strFormat === 'readable-date-and-time')
        return [
            format(d, 'E, LLLL d, yyyy', options),
            'at',
            format(d, 'h:mmaaa', options),
            timezoneString,
        ].join(' ');
    if (strFormat.includes('month-day')) {
        const month = format(d, 'LLLL', options);
        const dayOfMonth = format(d, 'd', options);
        return `${month} ${dayOfMonth}${getOrdinalSuffix(Number(dayOfMonth))}`;
    }
    return format(d, 'PPPP', options).slice(0, -6);
};
/** Curried Version of the formatDate function */
export const formatDateC = (format) => (date) => formatDate(format, date);
export const formatDateRange = ([firstFormat, secondFormat], [firstDate, secondDate], timezone, hideTimezone) => {
    if (!firstDate || !secondDate)
        return 'unknown date';
    const start = parseDateOrThrow(firstDate);
    const end = parseDateOrThrow(secondDate);
    return `${formatDate(firstFormat, start, timezone, true)} - ${formatDate(secondFormat || firstFormat, end, timezone, hideTimezone)}`;
};
// function getWeekOfMonth(date: Date) {
//   const startOfMonthDate = startOfMonth(date)
//   const weekStart = getWeek(startOfMonthDate)
//   const weekCurrent = getWeek(date)
//   // If the month starts in the middle of the week, adjust calculations accordingly
//   const firstDayOfMonth = startOfMonthDate.getDay() // Sunday is 0, Saturday is 6
//   const offset = firstDayOfMonth === 0 ? 0 : 1
//   return weekCurrent - weekStart + offset
// }
function getWeekOfMonth(date) {
    const start = startOfMonth(date);
    const dayOfMonth = getDate(date);
    const startWeekday = getDay(start); // 0-6
    return Math.ceil((dayOfMonth + startWeekday) / 7);
}
/**
 * Takes an object, usually an event product, and returns an array of events with
 * the dates based on the recurrence rule in the object. Note you have to pass in
 * event products with starttimes that are **before** the current date if they have
 * a recurrence rule that persists into the future. Returns event dates
 * that are within the specified range, or from now to now+(1 year) if not specified
 *
 * @param product
 * @param afterDate
 * @param beforeDate
 * @returns
 */
// export const expandEventProductDates = <T extends ObjectWithDates>(
//   product: T,
//   afterDate?: Date,
//   beforeDate?: Date,
// ): T[] => {
//   const expand = (product: T) => {
//     if (!product.recurrence || product.type === 'Series') return [product]
//     // we want to respect the before and after dates if they are passed in
//     // but overrides may cause the date returned by the rrule to be overridden
//     // to a date that is outside the before and after range. So we assume a
//     // date margin when fetching the rrule dates to hopefully catch any
//     // dates that are outside the range, but have been overriden to a time
//     // that is inside the range.
//     const dates = getRRuleDates(
//       product.recurrence,
//       subWeeks(5, afterDate ?? new Date(product.startTime)),
//       addWeeks(5, beforeDate ?? addYears(1, new Date(product.startTime))),
//     )
//       .map((date) => {
//         // our overrides prop is an object with keys that are ISO strings in the true UTC
//         // time zone. We need to convert the local date to a UTC date to match the key
//         const indexDate = date.toISOString()
//         return {
//           date: date,
//           startTime: product?.overrides?.[indexDate]?.startTime || date,
//           endTime:
//             product?.overrides?.[indexDate]?.endTime ||
//             addMinutes(
//               date,
//               differenceInMinutes(product.endTime, product.startTime),
//             ),
//         }
//       })
//       // add/sub a millisecond so that we included dates that are exactly the same
//       // (make inclusive)
//       .filter((d) =>
//         beforeDate
//           ? isBefore(d.startTime, addMilliseconds(beforeDate, 1))
//           : true,
//       )
//       .filter((d) =>
//         afterDate ? isAfter(d.startTime, subMilliseconds(afterDate, 1)) : true,
//       )
//     return dates.map((date) => ({
//       ...omit(['overrides'], product),
//       startTime: date.startTime,
//       endTime: date.endTime,
//       ...(product?.overrides
//         ? product.overrides?.[date.date.toISOString()]
//         : {}),
//     }))
//   }
//   return Array.isArray(product) ? product.flatMap(expand) : expand(product)
// }
/**
 * Takes an object, usually an event product, and returns the
 * specific occurance of that object for a given date, with any
 * relevant overrides applied. Returns an object or null if not
 * occurance exists on that date
 *
 * @param product
 * @param date the occurance date for which to search and apply overrides
 * @returns
 */
export const getEventProductOccurance = (product, date) => {
    // const expand = (product: T) => expandEventProductDates(product, date, date)
    // return Array.isArray(product) ? product.flatMap(expand) : expand(product)
};
export const removeTimeFromDate = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};
export const getLocalTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
export const parseDate = (date) => {
    const validOrUndefined = (date) => (isValid(date) ? date : undefined);
    if (isDate(date))
        return validOrUndefined(date);
    if (is(String, date))
        return validOrUndefined(new Date(date));
    if (is(Number, date))
        return validOrUndefined(new Date(date));
    return undefined;
};
export const parseDateOrThrow = (date, error) => {
    if (isDate(date))
        return date;
    // if (!isValid(date)) throw new Error(error || 'Not a valid date')
    if (is(String, date))
        return new Date(date);
    if (is(Number, date))
        return new Date(date);
    throw new Error(error || 'The date received is an invalid type');
};
export const anyDateEquals = (dates) => (date) => dates.some(isEqual(date));
/**
 * Recursively converts all dates in an object or array or data to ISO strings
 * Returns the input object unchanged if it is not an object, array, or date
 * @param input T: any
 * @returns T
 */
export const stringifyDatesDeep = R.cond([
    [R.is(Date), (date) => date.toISOString()],
    [R.is(Array), (input) => R.map(stringifyDatesDeep, input)],
    [R.is(Object), (input) => R.mapObjIndexed(stringifyDatesDeep, input)],
    [
        R.T,
        (input) => input, // Default case: return input unchanged
    ],
]);
/**
 *  Return the end of the month, plus 1 month if the passed date is
 * within 10 days of the end of the month
 *
 * @param currentDate Anchor for "now" — defaulted to `new Date()`; pass explicitly to keep this pure for tests.
 */
export const getEndOfMonthPlusMargin = (fromDate, currentDate = new Date()) => {
    const lastDayOfMonth = endOfMonth(fromDate);
    // If we aren't looking at the current month, just return one month of classes
    if (!isSameMonth(fromDate, currentDate))
        return lastDayOfMonth;
    const daysUntiLEnd = differenceInDays(lastDayOfMonth, currentDate);
    // If we are close to the end of this month, also show the classes for this month
    if (daysUntiLEnd < 10)
        return addMonths(1, endOfDay(currentDate));
    // otherwise, just show the classes for this month
    return lastDayOfMonth;
};
// const potentialDateKeys = [
//   'startTime',
//   'endTime',
//   'start',
//   'end',
//   'startDate',
//   'endDate',
// ] as const
// type PotentialDateKeys = (typeof potentialDateKeys)[number]
// type ObjectWithScheduleDates = Record<PotentialDateKeys, Date | TZDate>
// const convertDatesToTZDates = <T extends ObjectWithScheduleDates>(
//   object: T,
//   timezone?:
//     | string
//     | ({ in: (value: Date | number | string) => TZDate } & Record<any, any>),
// ) => {
//   R.reduce(
//     <T, K extends PotentialDateKeys>(object: T, key: K) => {
//       const test = pipe(
//         object,
//         R.ifElse(
//           (object): object is Record<K, Date | TZDate> =>
//             key in object && isDate(object[key]),
//           (object) => R.assoc(key, new TZDate(object[key]), object),
//           // as Omit<
//           //     T,
//           //     typeof key
//           //   > & { [typeof key]: TZDate },
//           R.identity,
//         ),
//         (e) => e,
//       )
//     },
//     object,
//     potentialDateKeys,
//   )
// }
const potentialDateKeys = [
    'startTime',
    'endTime',
    'start',
    'end',
    'startDate',
    'endDate',
];
/**
 * Converts all date values in an object at
 * @param object object with optional keys of type PotentialDateKeys
 * @param timezone
 * @param keysToConvert Which keys with Date values should be parsed as TZDates
 * @param widenTypes Sometimes needed to make types work in generic functions
 * @returns
 */
export const convertDatesToTZDates = (object, timezone, keysToConvert, widenTypes) => {
    const keys = (keysToConvert ??
        potentialDateKeys.filter((key) => key in object));
    return keys.reduce((acc, key) => {
        const value = acc[key];
        if (isDate(value)) {
            const converted = !timezone
                ? new TZDate(value, userTimezone)
                : typeof timezone === 'string'
                    ? new TZDate(value, timezone)
                    : timezone.in(value);
            return {
                ...acc,
                [key]: converted,
            };
        }
        return acc;
    }, object);
};
export const getDateOptionsFromTimezone = (timezone) => typeof timezone === 'string' ? { in: tz(timezone) } : (timezone ?? undefined);
//# sourceMappingURL=dates.js.map